// src/content/figures/Article27Decision.jsx
// Inline SVG infographic: does Article 27 GDPR apply to a non-EU company?
// Real text (indexable, selectable, screen-reader accessible).
// Explicit colours so it renders predictably regardless of theme tokens.

const C = {
  ink: '#1e293b',
  muted: '#5b6472',
  line: '#94a3b8',
  boxFill: '#f3f6fb',
  boxStroke: '#cbd5e1',
  white: '#ffffff',
  accent: '#186be6',
};

const q = { fill: C.boxFill, stroke: C.boxStroke, strokeWidth: 1.25, rx: 10 };
const end = { fill: C.white, stroke: C.boxStroke, strokeWidth: 1.25, rx: 10 };
const win = { fill: C.accent, stroke: 'none', rx: 10 };
const t = { fontFamily: 'inherit', fontSize: 15, fill: C.ink };
const s = { fontFamily: 'inherit', fontSize: 13, fill: C.muted };
const lab = { fontFamily: 'inherit', fontSize: 13, fill: C.ink, fontWeight: 600 };
const onAccent = { fontFamily: 'inherit', fontSize: 15, fill: C.white };
const edge = { stroke: C.line, strokeWidth: 1.5, fill: 'none', markerEnd: 'url(#a27-arrow)' };

export default function Article27Decision() {
  return (
    <svg
      viewBox="0 0 900 620"
      width="100%"
      role="img"
      aria-labelledby="a27-title a27-desc"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', maxWidth: 900 }}
    >
      <title id="a27-title">Does Article 27 GDPR apply to your company?</title>
      <desc id="a27-desc">
        Decision flow in three questions. One: do you have an establishment in the EU, meaning an office, branch or subsidiary carrying on real activity? If yes, Article 27 does not apply because the GDPR applies through Article 3(1). If no, question two: do you offer goods or services to people in the EU, or monitor their behaviour? Behavioural advertising and online tracking count as monitoring. If no, you are outside Article 3(2) and need no representative. If yes, question three: are all three exemption conditions true, namely occasional processing, no large-scale special-category or criminal-conviction data, and unlikely to pose a risk? Note that low volume is not the same as occasional: thirty customers served every month is regular processing. If all three are true you are exempt, which is rare for an operating business. Otherwise, appoint an EU representative in writing under Article 27(1).
      </desc>
      <defs>
        <marker id="a27-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={C.line} />
        </marker>
      </defs>

      {/* Column guide: left x=30–240 (c=135) · centre x=270–630 (c=450; Q2 and Q3 250–650) · right x=680–880 (c=780) */}

      {/* Q1 */}
      <rect x="270" y="20" width="360" height="64" {...q} />
      <text x="450" y="46" textAnchor="middle" {...t}>Do you have an establishment in the EU?</text>
      <text x="450" y="67" textAnchor="middle" {...s}>An office, branch or subsidiary carrying on real activity</text>

      {/* Q1 → Yes (left) */}
      <path d="M 270 52 L 135 52 L 135 140" {...edge} />
      <text x="145" y="104" {...lab}>Yes</text>
      <rect x="30" y="140" width="210" height="80" {...end} />
      <text x="135" y="168" textAnchor="middle" {...t}>Article 27 does not apply.</text>
      <text x="135" y="190" textAnchor="middle" {...s}>GDPR applies to you directly</text>
      <text x="135" y="208" textAnchor="middle" {...s}>through Article 3(1).</text>

      {/* Q1 → No (down) */}
      <path d="M 450 84 L 450 150" {...edge} />
      <text x="462" y="122" {...lab}>No</text>

      {/* Q2 */}
      <rect x="250" y="150" width="400" height="86" {...q} />
      <text x="450" y="176" textAnchor="middle" {...t}>Do you offer goods or services to people</text>
      <text x="450" y="197" textAnchor="middle" {...t}>in the EU, or monitor their behaviour?</text>
      <text x="450" y="218" textAnchor="middle" {...s}>Behavioural advertising and online tracking count as monitoring</text>

      {/* Q2 → No (right) */}
      <path d="M 650 193 L 780 193 L 780 260" {...edge} />
      <text x="690" y="184" {...lab}>No</text>
      <rect x="680" y="260" width="200" height="92" {...end} />
      <text x="780" y="286" textAnchor="middle" {...t}>Outside Article 3(2).</text>
      <text x="780" y="307" textAnchor="middle" {...s}>No representative needed.</text>
      <text x="780" y="325" textAnchor="middle" {...s}>Re-check if you later</text>
      <text x="780" y="342" textAnchor="middle" {...s}>sell to the EU.</text>

      {/* Q2 → Yes (down) */}
      <path d="M 450 236 L 450 300" {...edge} />
      <text x="462" y="273" {...lab}>Yes</text>

      {/* Q3 */}
      <rect x="250" y="300" width="400" height="150" {...q} />
      <text x="450" y="326" textAnchor="middle" {...t}>Are all three true? (Article 27(2)(a))</text>
      <text x="450" y="350" textAnchor="middle" {...s}>1. Occasional: a one-off, outside your regular business</text>
      <text x="450" y="370" textAnchor="middle" {...s}>2. No large-scale special-category or criminal data</text>
      <text x="450" y="390" textAnchor="middle" {...s}>3. Unlikely to result in a risk to individuals</text>
      <rect x="266" y="402" width="368" height="36" fill="#fff4e0" stroke="none" rx="6" />
      <text x="450" y="417" textAnchor="middle" fontFamily="inherit" fontSize="12.5" fill="#7a4a00" fontWeight="600">Low volume is not "occasional".</text>
      <text x="450" y="432" textAnchor="middle" fontFamily="inherit" fontSize="12.5" fill="#7a4a00">30 customers served every month is regular processing.</text>

      {/* Q3 → Yes, all three (left) */}
      <path d="M 250 375 L 130 375 L 130 500" {...edge} />
      <text x="140" y="450" {...lab}>Yes, all three</text>
      <rect x="20" y="500" width="220" height="80" {...end} />
      <text x="130" y="528" textAnchor="middle" {...t}>Exempt.</text>
      <text x="130" y="550" textAnchor="middle" {...s}>Rare for an operating business.</text>
      <text x="130" y="568" textAnchor="middle" {...s}>Document why; re-check yearly.</text>

      {/* Q3 → No (down) */}
      <path d="M 450 450 L 450 500" {...edge} />
      <text x="462" y="480" {...lab}>No</text>

      {/* Result */}
      <rect x="300" y="500" width="300" height="80" {...win} />
      <text x="450" y="532" textAnchor="middle" {...onAccent} fontWeight="600">Appoint an EU representative</text>
      <text x="450" y="555" textAnchor="middle" {...onAccent}>in writing, under Article 27(1).</text>
    </svg>
  );
}
